import {
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';

const HISTORY_START_THRESHOLD_PX = 1;

export type OlderHistoryScroll = {
    readonly identityKey: string;
    readonly hasOlder: boolean;
    readonly isLoading: boolean;
    readonly error: Error | null;
    readonly loadOlder: () => void;
    readonly retry: () => void;
};

type ScrollAnchor = {
    readonly element: HTMLElement;
    readonly top: number;
};

type UseOlderHistoryScrollOptions = {
    readonly contentChangeKey: unknown;
    readonly contentRef: RefObject<HTMLElement | null>;
    readonly getAnchorElements?: () => ReadonlyArray<HTMLElement>;
    readonly getEventElement?: () => HTMLElement | null;
    readonly getScrollElement: () => HTMLElement | null;
    readonly history?: OlderHistoryScroll;
    readonly initialScrollPosition?: 'start' | 'end';
    readonly onUserScrollTowardStart?: () => void;
};

type UseOlderHistoryScrollReturn = {
    readonly retry: () => void;
};

export function useOlderHistoryScroll({
    contentChangeKey,
    contentRef,
    getAnchorElements,
    getScrollElement,
    getEventElement = getScrollElement,
    history,
    initialScrollPosition = 'start',
    onUserScrollTowardStart,
}: UseOlderHistoryScrollOptions): UseOlderHistoryScrollReturn {
    const historyError = history?.error ?? null;
    const historyHasOlder = history?.hasOlder ?? false;
    const historyIdentityKey = history?.identityKey ?? null;
    const historyIsLoading = history?.isLoading ?? false;
    const loadOlder = history?.loadOlder;
    const retryOlder = history?.retry;
    const anchorRef = useRef<ReadonlyArray<ScrollAnchor>>([]);
    const gestureActiveRef = useRef(false);
    const gestureConsumedRef = useRef(false);
    const initializedIdentityRef = useRef<string | null>(null);
    const previousHistoryLoadingRef = useRef(false);
    const previousScrollTopRef = useRef(0);
    const previousTouchYRef = useRef<number | null>(null);

    const readAnchorElements = useCallback(() => {
        if (getAnchorElements) {
            return getAnchorElements();
        }
        return Array.from(contentRef.current?.children ?? []).filter(
            (element): element is HTMLElement => element instanceof HTMLElement,
        );
    }, [contentRef, getAnchorElements]);

    const captureAnchor = useCallback(() => {
        const scrollElement = getScrollElement();
        if (!scrollElement) {
            return;
        }
        const viewport = scrollElement.getBoundingClientRect();
        const anchors: Array<ScrollAnchor> = [];
        for (const element of readAnchorElements()) {
            const bounds = element.getBoundingClientRect();
            if (bounds.height > 0 && bounds.bottom > viewport.top) {
                anchors.push({ element, top: bounds.top - viewport.top });
                if (bounds.top >= viewport.bottom) {
                    break;
                }
            }
        }
        anchorRef.current = anchors;
    }, [getScrollElement, readAnchorElements]);
    const retry = useCallback(() => {
        if (!retryOlder) {
            return;
        }
        captureAnchor();
        retryOlder();
    }, [captureAnchor, retryOlder]);

    const loadAtStart = useCallback(() => {
        const scrollElement = getScrollElement();
        if (
            !scrollElement ||
            !loadOlder ||
            gestureConsumedRef.current ||
            scrollElement.scrollTop > HISTORY_START_THRESHOLD_PX ||
            !historyHasOlder ||
            historyIsLoading ||
            historyError !== null
        ) {
            return;
        }
        gestureConsumedRef.current = true;
        captureAnchor();
        loadOlder();
    }, [
        captureAnchor,
        getScrollElement,
        historyError,
        historyHasOlder,
        historyIsLoading,
        loadOlder,
    ]);

    useLayoutEffect(() => {
        anchorRef.current = [];
        gestureActiveRef.current = false;
        gestureConsumedRef.current = false;
        previousHistoryLoadingRef.current = false;
        previousScrollTopRef.current = getScrollElement()?.scrollTop ?? 0;
    }, [getScrollElement, historyIdentityKey]);

    useLayoutEffect(() => {
        const scrollElement = getScrollElement();
        const anchor = anchorRef.current.find(({ element }) =>
            contentRef.current?.contains(element),
        );
        const olderRequestCompleted =
            previousHistoryLoadingRef.current && !historyIsLoading;
        previousHistoryLoadingRef.current = historyIsLoading;
        if (olderRequestCompleted) {
            gestureActiveRef.current = false;
        }
        if (scrollElement && anchor) {
            const currentTop =
                anchor.element.getBoundingClientRect().top -
                scrollElement.getBoundingClientRect().top;
            scrollElement.scrollTop += currentTop - anchor.top;
            previousScrollTopRef.current = scrollElement.scrollTop;
        }
        if (historyIdentityKey !== null && !historyIsLoading) {
            anchorRef.current = [];
            gestureConsumedRef.current = false;
        }
    }, [
        contentChangeKey,
        contentRef,
        getScrollElement,
        historyIdentityKey,
        historyIsLoading,
    ]);

    useLayoutEffect(() => {
        const scrollElement = getScrollElement();
        if (
            initialScrollPosition !== 'end' ||
            !scrollElement ||
            historyIdentityKey === null ||
            initializedIdentityRef.current === historyIdentityKey ||
            readAnchorElements().length === 0
        ) {
            return;
        }
        scrollElement.scrollTop = scrollElement.scrollHeight;
        previousScrollTopRef.current = scrollElement.scrollTop;
        initializedIdentityRef.current = historyIdentityKey;
    }, [
        contentChangeKey,
        getScrollElement,
        historyIdentityKey,
        initialScrollPosition,
        readAnchorElements,
    ]);

    useEffect(() => {
        const scrollElement = getScrollElement();
        const eventElement = getEventElement();
        if (!scrollElement || !eventElement || historyIdentityKey === null) {
            return;
        }

        const markTowardStart = () => {
            gestureActiveRef.current = true;
            onUserScrollTowardStart?.();
        };
        const handleScroll = () => {
            const movedTowardStart =
                scrollElement.scrollTop < previousScrollTopRef.current;
            previousScrollTopRef.current = scrollElement.scrollTop;
            if (gestureActiveRef.current && historyIsLoading) {
                captureAnchor();
            }
            if (gestureActiveRef.current && movedTowardStart) {
                loadAtStart();
            }
        };
        const handleWheel = (event: WheelEvent) => {
            if (event.deltaY < 0) {
                markTowardStart();
                loadAtStart();
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === 'ArrowUp' ||
                event.key === 'Home' ||
                event.key === 'PageUp'
            ) {
                markTowardStart();
                loadAtStart();
            }
        };
        const handlePointerDown = () => {
            gestureActiveRef.current = true;
        };
        const handlePointerMove = (event: PointerEvent) => {
            if (event.buttons > 0) {
                markTowardStart();
            }
        };
        const handleGestureEnd = () => {
            gestureActiveRef.current = false;
            previousTouchYRef.current = null;
        };
        const handleTouchStart = (event: TouchEvent) => {
            gestureActiveRef.current = true;
            previousTouchYRef.current = event.touches[0]?.clientY ?? null;
        };
        const handleTouchMove = (event: TouchEvent) => {
            const touchY = event.touches[0]?.clientY;
            const previousTouchY = previousTouchYRef.current;
            if (touchY === undefined || previousTouchY === null) {
                return;
            }
            previousTouchYRef.current = touchY;
            if (touchY > previousTouchY) {
                markTowardStart();
                loadAtStart();
            }
        };

        scrollElement.addEventListener('scroll', handleScroll, {
            passive: true,
        });
        eventElement.addEventListener('wheel', handleWheel, { passive: true });
        eventElement.addEventListener('keydown', handleKeyDown);
        eventElement.addEventListener('pointerdown', handlePointerDown);
        eventElement.addEventListener('pointermove', handlePointerMove);
        eventElement.addEventListener('pointerup', handleGestureEnd);
        eventElement.addEventListener('pointercancel', handleGestureEnd);
        scrollElement.addEventListener('scrollend', handleGestureEnd);
        eventElement.addEventListener('touchstart', handleTouchStart, {
            passive: true,
        });
        eventElement.addEventListener('touchmove', handleTouchMove, {
            passive: true,
        });
        eventElement.addEventListener('touchend', handleGestureEnd);
        eventElement.addEventListener('touchcancel', handleGestureEnd);
        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
            eventElement.removeEventListener('wheel', handleWheel);
            eventElement.removeEventListener('keydown', handleKeyDown);
            eventElement.removeEventListener('pointerdown', handlePointerDown);
            eventElement.removeEventListener('pointermove', handlePointerMove);
            eventElement.removeEventListener('pointerup', handleGestureEnd);
            eventElement.removeEventListener('pointercancel', handleGestureEnd);
            scrollElement.removeEventListener('scrollend', handleGestureEnd);
            eventElement.removeEventListener('touchstart', handleTouchStart);
            eventElement.removeEventListener('touchmove', handleTouchMove);
            eventElement.removeEventListener('touchend', handleGestureEnd);
            eventElement.removeEventListener('touchcancel', handleGestureEnd);
        };
    }, [
        captureAnchor,
        getEventElement,
        getScrollElement,
        historyIdentityKey,
        historyIsLoading,
        loadAtStart,
        onUserScrollTowardStart,
    ]);

    return { retry };
}
